import { FC } from 'react';
import { formatCurrency } from '../../utils/format/formatCurrency';
import { useExperimentFlag } from '../../utils/useExperimentFlag';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';
import { FintechDiscountBadge } from './FintechDiscountBadge/FintechDiscountBadge';
import { NumberTypography } from './styles';
import { PriceProps } from './types';

export const Price: FC<PriceProps> = ({
    priceInfo,
    oldPriceInfo,
    hideOldPrice,
    oldPriceOptions = {},
    fintechDiscount,
    fintechDiscountOptions = {},
    showOldPriceBadge,
    ...other
}) => {
    const discountData = fintechDiscount?.yaPay || fintechDiscount?.yaSplit;
    const discountEnabled = Number(useExperimentFlag('fintech_discount_enabled')) === 1;
    const hasDiscount = Boolean(discountData && discountEnabled);
    const shouldShowOldPriceBadge =
        showOldPriceBadge && !hasDiscount && oldPriceInfo?.discountPercent;
    const currentPrice = hasDiscount ? discountData?.discountPrice : priceInfo?.integerPart;

    const oldPriceValue = hideOldPrice ? undefined : oldPriceInfo?.integerPart;

    if (!currentPrice || !priceInfo?.integerPart) {
        return null;
    }

    return (
        <Flex direction="row" alignItems="center" flexWrap="wrap">
            <NumberTypography
                variant="body"
                weight="bold"
                lines={1}
                mr={0.75}
                {...other}
                color={hasDiscount ? 'var(--search-red)' : other.color || 'var(--color-primary)'}
            >
                {priceInfo.formatted ? priceInfo.formatted : formatCurrency(currentPrice)}
            </NumberTypography>
            {oldPriceValue ? (
                <NumberTypography
                    variant={'body2'}
                    color="var(--control-disabled-color)"
                    textTransform="none"
                    textDecoration="line-through"
                    lines={1}
                    mr={0.5}
                    {...oldPriceOptions}
                >
                    {formatCurrency(
                        oldPriceValue,
                        hasDiscount ? priceInfo?.fractionalPart : oldPriceInfo?.fractionalPart,
                    )}
                </NumberTypography>
            ) : null}
            {shouldShowOldPriceBadge ? (
                <Typography
                    variant="caption2"
                    color={oldPriceOptions.color || 'var(--color-primary)'}
                    border={`1px solid ${oldPriceOptions.borderColor || 'var(--divider)'}`}
                    borderRadius={0.75}
                    px={0.5}
                >
                    {`–${oldPriceInfo.discountPercent}%`}
                </Typography>
            ) : null}
            {hasDiscount && fintechDiscount ? (
                <Flex>
                    <FintechDiscountBadge
                        fintechDiscount={fintechDiscount}
                        {...fintechDiscountOptions}
                    />
                </Flex>
            ) : null}
        </Flex>
    );
};
